import { resolvedPromiseLike } from '@conduit-client/utils';
import type { FetchParameters, RequestInterceptor } from './fetch';

let textEncoder: TextEncoder | undefined;

/**
 * Lazily initializes and returns the TextEncoder instance.
 * This ensures the module can be imported even in environments without TextEncoder,
 * and only fails when compression is actually attempted.
 */
function getTextEncoder(): TextEncoder {
    if (!textEncoder) {
        if (typeof TextEncoder === 'undefined') {
            throw new Error(
                'TextEncoder is not available in this environment. ' +
                    'Request body compression requires TextEncoder support.'
            );
        }
        textEncoder = new TextEncoder();
    }
    return textEncoder;
}

/**
 * Configuration for the compression interceptor.
 */
export interface CompressionInterceptorConfig {
    /**
     * Compression algorithm. Currently only 'gzip' is supported.
     */
    algorithm: 'gzip';

    /**
     * Minimum body size in bytes to trigger compression.
     * Bodies smaller than this threshold are sent uncompressed.
     * @default 1024
     */
    threshold?: number;
}

/**
 * Builds a request interceptor that compresses string request bodies.
 *
 * The interceptor only compresses when all conditions are met:
 * - The request has a body
 * - The body is a string
 * - The body size exceeds the configured threshold
 * - The runtime supports CompressionStream
 * - Content-Encoding is not already set
 *
 * If compression fails for any reason, the request is sent uncompressed.
 *
 * IMPORTANT: This interceptor should run LAST in the request interceptor chain,
 * after any other interceptors that might modify the request body.
 *
 * @param config - Compression configuration
 * @returns A request interceptor that compresses eligible request bodies
 */
export function buildCompressionInterceptor(
    config: CompressionInterceptorConfig
): RequestInterceptor {
    const threshold = config.threshold ?? 1024;

    return async (args: FetchParameters) => {
        const [resource, options = {}] = args;

        // Only compress string bodies
        if (!options.body || typeof options.body !== 'string') {
            return resolvedPromiseLike(args);
        }

        // Check if CompressionStream is available in this runtime
        if (typeof CompressionStream === 'undefined') {
            return resolvedPromiseLike(args);
        }

        // Normalize headers once — reuse for check and mutation
        const headers = new Headers(options.headers);

        // Skip if Content-Encoding is already set (don't double-encode)
        if (headers.has('Content-Encoding')) {
            return resolvedPromiseLike(args);
        }

        // Encode the body once and check threshold
        const encodedBody = getTextEncoder().encode(options.body);
        if (encodedBody.byteLength < threshold) {
            return resolvedPromiseLike(args);
        }

        try {
            // Compress the body using CompressionStream (reuse encoded bytes)
            const stream = new Blob([encodedBody])
                .stream()
                .pipeThrough(new CompressionStream(config.algorithm));

            const compressedBody = await new Response(stream).blob();

            // Set Content-Encoding and remove Content-Length (now invalid after compression)
            headers.set('Content-Encoding', config.algorithm);
            headers.delete('Content-Length');

            const compressedOptions: RequestInit = {
                ...options,
                body: compressedBody,
                headers,
            };

            return resolvedPromiseLike<FetchParameters>([resource, compressedOptions]);
        } catch {
            // If compression fails for any reason, fall back to uncompressed
            // This ensures requests are never broken by compression failures
            return resolvedPromiseLike(args);
        }
    };
}
